import { localStorage } from '#utils/localStorage';

/**
 * Какой микрофон, динамик и камеру человек выбрал на ЭТОМ устройстве.
 *
 * <p>До сих пор выбор не переживал даже перезагрузку страницы: и меню в голосе, и меню в звонке
 * держали его в своём `useState`, так что человек с двумя микрофонами выбирал нужный заново перед
 * каждым разговором.
 *
 * <p>Хранится на устройстве, а не в профиле, и это осознанно: за 90 дней 781 человек из 794
 * заходил ровно с одной платформы. Тащить «микрофон гарнитуры» с компьютера на телефон, где такой
 * гарнитуры нет, значило бы каждый раз выбирать несуществующее устройство — хуже, чем не помнить
 * вовсе.
 */

export type MediaDeviceRole = 'mic' | 'speaker' | 'camera';

const KEYS: Record<MediaDeviceRole, string> = {
  mic: 'media.device.mic',
  speaker: 'media.device.speaker',
  camera: 'media.device.camera',
};

export const loadDevicePreference = (role: MediaDeviceRole): string | null =>
  localStorage.getItem(KEYS[role]) ?? null;

export const saveDevicePreference = (role: MediaDeviceRole, deviceId: string): void => {
  if (!deviceId) {
    localStorage.deleteItem(KEYS[role]);
    return;
  }
  localStorage.setItem(KEYS[role], deviceId);
};

/**
 * Какое устройство считать выбранным сейчас.
 *
 * <p>Запомненного может уже не быть: гарнитуру вынули, вебкамеру забрал другой ноутбук, а
 * `deviceId` Safari меняет между сеансами вовсе. Поэтому запомненный идентификатор — это
 * пожелание, а не приказ: нет его в списке — отдаём системный выбор (`default`, если браузер такой
 * отдаёт, иначе первое устройство), а не пустоту, из-за которой список выглядел бы пустым при
 * живом микрофоне.
 */
export const resolvePreferredDevice = (
  devices: readonly MediaDeviceInfo[],
  preferred: string | null
): string | undefined => {
  if (devices.length === 0) {
    return undefined;
  }
  if (preferred && devices.some((device) => device.deviceId === preferred)) {
    return preferred;
  }
  const systemDefault = devices.find((device) => device.deviceId === 'default');
  return (systemDefault ?? devices[0]).deviceId;
};
